import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api } from '../api.js';
import { mediumDate, money, count } from '../lib/format.js';
import { SIZE_TIERS, sizeTierLabel } from '../lib/size.js';

/** Workamajig's own dropdown, exactly: 1 to 10, and nothing else (Estimate Tool 3.1). */
const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * Data Hygiene (Estimate Tool 9; Phase E2): every closed, billed website project, newest first,
 * with the two ratings its *client* carries, what it billed, and whether it can price anything.
 *
 * The screen exists because of one number: 39 of 202 website projects can be used as analogs,
 * and the reason most of the rest cannot is that nobody has rated their client. So the funnel is
 * stated above the table — every website project down to the usable pool — and the rows that a
 * rating would rescue are red.
 *
 * The table holds only what the screen can act on. A project that is still open, was cancelled,
 * or billed nothing is counted in the funnel and is not a row: none of the three is a question
 * this screen can answer, and between them they were two thirds of the table. A project marked
 * Do Not Use is not a row either — that question has been answered, by a person — but **Show
 * all** brings those back, because a decision that cannot be reversed is not a decision, and the
 * box has to be reachable to be cleared.
 *
 * What is left is the set where the remaining questions are the two a reader can settle here —
 * is the client rated, and should this project count at all.
 *
 * Red is reserved for "client is unrated" (9.2), because it is the only exclusion this screen
 * can fix. Every other reason gets its own words: a cancelled project is correctly excluded, a
 * zero-gross one is a billing question in Workamajig, an unresolved client name is a data
 * question there. Colour is never the only signal — the Usable column always says the reason in
 * words, and red rows carry a marker glyph besides.
 *
 * **Do Not Use** is the second thing a reader can settle. Every other exclusion is derived from
 * a fact about the project; this one is a judgment — the project is complete, billed and rated,
 * and it still does not represent the work. Ticking it takes the project out of the analog pool
 * and out of schedule calibration, and the funnel above re-counts from the server's answer
 * rather than from this component's arithmetic, so the number on screen is never one render
 * ahead of the pool.
 *
 * Nothing here calls Workamajig. `GET /estimate/hygiene` reads local tables only, and the
 * verdict on every row is the same `usability()` the analog model selects with, so the count
 * this screen shows and the pool the price comes from cannot disagree.
 */
class EstimateHygiene extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _writeError: { state: true },
    _filter: { state: true },
    _saving: { state: true },
    _editing: { state: true },
    _editingRow: { state: true },
    _draft: { state: true },
    _showAll: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    this._writeError = null;
    // Everything, so the size of the problem is the first thing seen (9.1).
    this._filter = 'all';
    // A project marked "Do Not Use" is out of the pool by a decision that has already been
    // taken, so it is not work in front of anyone. Show all brings them back to be un-ticked.
    this._showAll = false;
    this._saving = new Set();
    // The company being rated, the row the editor opened on, and the pair chosen. One at a
    // time: a write moves every project the client owns, so two half-filled editors would be
    // two loaded guns. The row matters as well as the company because a client can own many —
    // Superior Energy owns fourteen — and the editor belongs under the one that was clicked.
    this._editing = null;
    this._editingRow = null;
    this._draft = { org_complexity: null, human_factor: null };
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 6px 0 4px;
      font-size: 20px;
      color: var(--ink-strong);
    }
    .lede {
      margin: 0 0 16px;
      color: var(--ink-muted);
      font-size: 13.5px;
      max-width: 70ch;
    }

    /* The funnel of Estimate Tool 7.1, left to right: every website project down to the pool. */
    .funnel {
      display: flex;
      flex-wrap: wrap;
      gap: 2px 0;
      margin-bottom: 16px;
      border: 1px solid var(--border-soft);
      border-radius: 8px;
      overflow: hidden;
    }
    .step {
      flex: 1 1 auto;
      min-width: 128px;
      padding: 10px 14px;
      border-right: 1px solid var(--border-soft);
    }
    .step:last-child {
      border-right: 0;
    }
    .step .n {
      font-size: 22px;
      font-weight: 700;
      color: var(--ink-strong);
      font-variant-numeric: tabular-nums;
      line-height: 1.1;
    }
    .step .k {
      font-size: 12px;
      color: var(--ink-muted);
    }
    .step.pool {
      background: var(--surface-2);
    }
    .step.pool .n {
      color: var(--accent-strong, var(--ink-strong));
    }

    .controls {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 16px;
      margin-bottom: 12px;
    }
    .segmented {
      display: inline-flex;
      border: 1px solid var(--control-border);
      border-radius: var(--radius-pill);
      background: var(--control-bg);
      padding: 2px;
    }
    .segmented button {
      font: inherit;
      font-size: 12.5px;
      font-weight: 600;
      color: var(--ink-muted);
      background: none;
      border: 0;
      border-radius: var(--radius-pill);
      padding: 4px 12px;
      cursor: pointer;
    }
    .segmented button[aria-pressed='true'] {
      color: var(--surface);
      background: var(--ink-strong);
    }
    .shown {
      font-size: 12.5px;
      color: var(--ink-muted);
    }
    .show-all {
      font: inherit;
      font-size: 12.5px;
      font-weight: 600;
      padding: 4px 12px;
      border: 1px solid var(--control-border);
      border-radius: var(--radius-pill);
      background: var(--control-bg);
      color: var(--ink-strong);
      cursor: pointer;
    }
    .show-all:hover {
      background: var(--surface-2);
    }
    .link {
      font: inherit;
      padding: 0;
      border: 0;
      background: none;
      color: inherit;
      text-decoration: underline;
      cursor: pointer;
    }

    .scroll {
      overflow-x: auto;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      font-size: 13px;
    }
    th,
    td {
      text-align: left;
      padding: 7px 10px;
      border-bottom: 1px solid var(--border-soft);
      white-space: nowrap;
    }
    th {
      font-size: 11.5px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--ink-muted);
      font-weight: 700;
      position: sticky;
      top: 0;
      background: var(--surface);
    }
    td.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    td.project {
      white-space: normal;
      min-width: 22ch;
    }
    .num-mono {
      font-family: var(--font-eyebrow);
      font-size: 11.5px;
      color: var(--ink-muted);
    }

    /* Red rows: a tinted row, a left rule, and a marker before the reason. Never colour alone. */
    tr.red td {
      background: var(--status-overloaded-soft, rgba(200, 40, 40, 0.07));
    }
    tr.red td:first-child {
      box-shadow: inset 3px 0 0 var(--status-overloaded);
    }
    .reason {
      display: inline-flex;
      align-items: baseline;
      gap: 6px;
    }
    .reason .marker {
      font-weight: 800;
      color: var(--status-overloaded);
    }
    .usable {
      color: var(--status-healthy);
      font-weight: 600;
    }
    /* An excluded row is dimmed, not hidden: it has to stay findable to be un-excluded. */
    tr.excluded td {
      opacity: 0.62;
    }
    tr.excluded td.use {
      opacity: 1;
    }
    td.use,
    th.use {
      text-align: center;
      width: 1%;
    }
    td.use input {
      width: 16px;
      height: 16px;
      cursor: pointer;
      accent-color: var(--status-overloaded);
    }
    td.use input:disabled {
      cursor: progress;
    }
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      white-space: nowrap;
      border: 0;
    }
    .note {
      display: block;
      font-size: 11.5px;
      color: var(--ink-muted);
      white-space: normal;
    }
    .unrated {
      color: var(--ink-muted);
    }
    .empty,
    .state {
      padding: 20px 0;
      color: var(--ink-muted);
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
    }
    /* The in-place editor, in a row of its own beneath the one being rated. */
    .rate {
      display: inline-block;
      margin-top: 4px;
      font: inherit;
      font-size: 12px;
      font-weight: 600;
      padding: 3px 10px;
      border: 1px solid var(--control-border);
      border-radius: var(--radius-pill);
      background: var(--control-bg);
      color: var(--ink-strong);
      cursor: pointer;
    }
    .rate:hover {
      background: var(--surface-2);
    }
    tr.editor-row td {
      background: var(--surface-2);
      white-space: normal;
      box-shadow: inset 3px 0 0 var(--status-overloaded);
    }
    .editor {
      display: grid;
      gap: 10px;
      max-width: 70ch;
      padding: 4px 0;
    }
    .fields {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
    }
    .field {
      display: grid;
      gap: 4px;
      font-size: 12px;
      color: var(--ink-muted);
      font-weight: 600;
    }
    .field select {
      font: inherit;
      font-size: 14px;
      padding: 5px 8px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--control-bg);
      color: var(--ink-strong);
      min-width: 8ch;
    }
    /* The sentence a planner has to read before the confirm: what the write actually reaches. */
    .blast {
      margin: 0;
      font-size: 13px;
      color: var(--ink-strong);
      line-height: 1.45;
    }
    .editor-actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 12px;
    }
    .editor-actions button {
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      padding: 6px 14px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--surface);
      color: var(--ink-strong);
      cursor: pointer;
    }
    .editor-actions button.primary {
      background: var(--ink-strong);
      color: var(--surface);
      border-color: var(--ink-strong);
    }
    td.size select {
      font: inherit;
      font-size: 12px;
      padding: 3px 5px;
      border: 1px solid var(--control-border);
      border-radius: 5px;
      background: var(--control-bg);
      color: var(--ink-strong);
      max-width: 15ch;
    }
    .src {
      display: inline-block;
      margin-left: 5px;
      padding: 0 4px;
      border: 1px solid var(--border);
      border-radius: 3px;
      font-family: var(--font-eyebrow);
      font-size: 9.5px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--ink-muted);
      vertical-align: middle;
    }
    .editor-actions button.danger {
      border-color: var(--status-overloaded);
      color: var(--status-overloaded);
    }
    .editor-actions button[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .editor-actions .hint {
      font-size: 12px;
      color: var(--ink-muted);
    }
    /* A standing condition, not a failure of something the reader just did. */
    .notice {
      margin: 0 0 12px;
      padding: 8px 12px;
      border: 1px solid var(--border);
      border-left: 3px solid var(--status-caution, var(--ink-muted));
      border-radius: 6px;
      background: var(--surface-2);
      color: var(--ink);
      font-size: 13px;
      max-width: 80ch;
    }
    .write-error {
      margin: 0 0 12px;
      padding: 8px 12px;
      border: 1px solid var(--status-overloaded);
      border-radius: 6px;
      color: var(--status-overloaded);
      font-size: 13px;
      font-weight: 600;
    }
    @media (max-width: 700px) {
      .step {
        min-width: 45%;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    this._loading = true;
    this._error = null;
    try {
      this._data = await api.get('/estimate/hygiene');
    } catch (err) {
      this._error = `Could not load Data Hygiene: ${err.message}`;
    } finally {
      this._loading = false;
    }
  }

  /**
   * Tick or clear Do Not Use. The server answers with the row and the recomputed funnel, and
   * both are taken from that answer: an optimistic update here could leave the count on screen
   * disagreeing with the pool a price is about to be computed from.
   */
  async _setExcluded(row, excluded) {
    if (this._saving.has(row.id)) return;
    this._saving = new Set([...this._saving, row.id]);
    this.requestUpdate();
    try {
      const res = await api.put(`/estimate/projects/${row.id}/exclusion`, { excluded });
      this._data = {
        ...this._data,
        counts: res.counts,
        projects: this._data.projects.map((r) => (r.id === row.id ? res.project : r)),
      };
      this._writeError = null;
    } catch (err) {
      // A failed write is reported above the table, not in place of it: losing 105 rows because
      // one checkbox did not save would be a worse outcome than the failure itself.
      this._writeError = `Could not update ${row.number ?? 'the project'}: ${err.message}`;
    } finally {
      const next = new Set(this._saving);
      next.delete(row.id);
      this._saving = next;
    }
  }

  get _canWrite() {
    return ['planner', 'admin'].includes(this.appState.user?.effective_role);
  }

  /** Whether the rating editor may be offered at all (the server decides; see ratings.js). */
  get _canRate() {
    return this._canWrite && this._data?.rating_write?.enabled !== false;
  }

  /**
   * Whether this row's rating is Workamajig's, and therefore whether editing it here can move
   * the price. Workamajig wins where it has a pair (Estimate Tool 9.3), so a local rating typed
   * onto an already-rated client is stored and shadowed — worth saying before the typing, not
   * after.
   */
  static _shadowed(row) {
    return row.rating_source === 'workamajig';
  }

  /**
   * Set or clear a project's size (Estimate Tool 7.2).
   *
   * The same shape as Do Not Use, deliberately: one PUT, and the counts and the row come back
   * from the server rather than being patched here. A size does not move the usable count, but
   * it does move which projects weigh most in every future price, so it is a planner action and
   * it is audited.
   *
   * The empty option clears the override rather than setting a value, which is what lets the
   * derived size come back.
   */
  async _setSize(row, value) {
    const key = `size:${row.id}`;
    if (this._saving.has(key)) return;
    this._saving = new Set([...this._saving, key]);
    try {
      const res = await api.put(`/estimate/projects/${row.id}/size`, {
        size_tier: value || null,
      });
      this._data = {
        ...this._data,
        counts: res.counts,
        projects: this._data.projects.map((r) => (r.id === row.id ? res.project : r)),
      };
      this._writeError = null;
    } catch (err) {
      this._writeError = `Could not size ${row.number ?? 'the project'}: ${err.message}`;
    } finally {
      const next = new Set(this._saving);
      next.delete(key);
      this._saving = next;
    }
  }

  _startEditing(row) {
    this._editing = row.company_id;
    this._editingRow = row.id;
    // The editor edits the **local** pair, so it opens on the local pair, not on the effective
    // one: showing Workamajig's numbers in boxes that cannot change them would invite exactly
    // the edit that does nothing.
    this._draft = {
      org_complexity: row.local_org_complexity ?? null,
      human_factor: row.local_human_factor ?? null,
    };
    this._writeError = null;
  }

  _cancelEditing() {
    this._editing = null;
    this._editingRow = null;
    this._draft = { org_complexity: null, human_factor: null };
  }

  /**
   * Store both ratings **here** (Estimate Tool 9.3, amended 2026-09-20). Nothing is sent to
   * Workamajig — the write that used to go there is gone, not merely disabled.
   *
   * The response carries the client's every row and the recomputed funnel, and both are taken
   * from it: one rating changes every project that client owns, and the table has to show that
   * without the reader refetching to find out.
   *
   * `clear` removes the local pair, which is the only way back from a number entered by
   * mistake.
   */
  async _saveRatings(row, { clear = false } = {}) {
    const key = `company:${row.company_id}`;
    if (this._saving.has(key)) return;
    this._saving = new Set([...this._saving, key]);
    this._writeError = null;
    try {
      const res = await api.put(`/estimate/companies/${row.company_id}/ratings`, {
        org_complexity: clear ? null : Number(this._draft.org_complexity),
        human_factor: clear ? null : Number(this._draft.human_factor),
      });
      const updated = new Map(res.projects.map((r) => [r.id, r]));
      this._data = {
        ...this._data,
        counts: res.counts,
        projects: this._data.projects.map((r) => updated.get(r.id) ?? r),
      };
      this._cancelEditing();
      // Not a failure — the pair was stored. It is the one outcome worth a sentence: the
      // client is rated in Workamajig too, and that is the pair the price uses.
      this._writeError = res.error;
    } catch (err) {
      this._writeError = `Could not rate ${row.client}: ${err.message}`;
    } finally {
      const next = new Set(this._saving);
      next.delete(key);
      this._saving = next;
    }
  }

  /**
   * The editor, and the sentence that has to be read before the confirm (Estimate Tool 9.3).
   *
   * The ratings live on the **company**, not the project, so one save reaches every project that
   * client owns. A per-project editor that quietly wrote a client-wide value would be the worst
   * available outcome — the reader would believe they had rated one engagement — so the row says
   * what the write does, in numbers checkable against the table, and the button is the confirm.
   */
  _editor(row) {
    const saving = this._saving.has(`company:${row.company_id}`);
    const chosen =
      RATINGS.includes(Number(this._draft.org_complexity)) &&
      RATINGS.includes(Number(this._draft.human_factor));
    // Storing the pair this client already carries locally would be a write with nothing to
    // show for it.
    const unchanged =
      chosen &&
      Number(this._draft.org_complexity) === row.local_org_complexity &&
      Number(this._draft.human_factor) === row.local_human_factor;
    const ready = chosen && !unchanged;
    const hasLocal = row.local_org_complexity !== null && row.local_human_factor !== null;
    // Workamajig holds a pair of its own for this client, so whatever is typed here is stored
    // and shadowed. Said before the typing rather than after it.
    const shadowed = EstimateHygiene._shadowed(row);
    const others = row.client_website_projects;
    const all = row.client_all_projects;

    const select = (field, label) => html`
      <label class="field">
        <span>${label}</span>
        <select
          .value=${String(this._draft[field] ?? '')}
          ?disabled=${saving}
          @change=${(e) => (this._draft = { ...this._draft, [field]: e.target.value })}
        >
          <option value="">—</option>
          ${RATINGS.map(
            (n) =>
              html`<option value=${n} ?selected=${Number(this._draft[field]) === n}>${n}</option>`,
          )}
        </select>
      </label>
    `;

    return html`
      <tr class="editor-row">
        <td colspan="9">
          <div class="editor">
            <div class="fields">
              ${select('org_complexity', 'Organizational complexity')}
              ${select('human_factor', 'Human factor')}
            </div>
            <p class="blast">
              ${
                hasLocal
                  ? html`This <strong>changes</strong> Organizational Complexity from
                      ${row.local_org_complexity} to ${this._draft.org_complexity || '—'} and Human
                      Factor from ${row.local_human_factor} to ${this._draft.human_factor || '—'}
                      for`
                  : html`This sets Organizational Complexity and Human Factor for`
              }
              <strong>${row.client}</strong> <strong>in WaaG only</strong>. Nothing is written to
              Workamajig. It applies to
              <strong
                >all ${count(others)} of their website
                ${others === 1 ? 'project' : 'projects'}</strong
              >, and to every other project they
              own${all > others ? html` — ${count(all)} in total` : nothing}.
              ${
                row.rated
                  ? html`Every estimate already priced against
                    ${others === 1 ? 'that project' : 'those projects'} keeps the numbers it was
                    saved with.`
                  : nothing
              }
            </p>
            ${
              shadowed
                ? html`<p class="blast" role="note">
                    <strong
                      >${row.client} is already rated in Workamajig at
                      ${row.workamajig_org_complexity} / ${row.workamajig_human_factor}, and that is
                      the pair that prices.</strong
                    >
                    Workamajig is the system of record for a client, so what you set here is kept
                    beside it and shown, but it will not move a price while Workamajig holds a pair
                    of its own. To change what prices, change it in Workamajig and re-sync.
                  </p>`
                : nothing
            }
            <div class="editor-actions">
              <button
                class="primary"
                ?disabled=${!ready || saving}
                @click=${() => this._saveRatings(row)}
              >
                ${saving ? 'Saving…' : hasLocal ? 'Confirm and change' : 'Confirm and rate'}
              </button>
              <button ?disabled=${saving} @click=${() => this._cancelEditing()}>Cancel</button>
              ${
                hasLocal
                  ? html`<button
                      class="danger"
                      ?disabled=${saving}
                      @click=${() => this._saveRatings(row, { clear: true })}
                    >
                      Clear the rating
                    </button>`
                  : nothing
              }
              ${
                unchanged
                  ? html`<span class="hint">That is what ${row.client} already carries.</span>`
                  : chosen
                    ? nothing
                    : html`<span class="hint"
                        >Both ratings are required: an estimate needs both axes, and half a pair
                        prices nothing.</span
                      >`
              }
            </div>
          </div>
        </td>
      </tr>
    `;
  }

  /** The rows before the rated/unrated filter: everything, less the excluded unless asked. */
  get _visible() {
    const all = this._data?.projects ?? [];
    return this._showAll ? all : all.filter((r) => !r.excluded);
  }

  /** How many rows "Show all" would add. */
  get _hidden() {
    if (this._showAll) return 0;
    return (this._data?.projects ?? []).filter((r) => r.excluded).length;
  }

  get _rows() {
    const visible = this._visible;
    if (this._filter === 'rated') return visible.filter((r) => r.rated);
    if (this._filter === 'unrated') return visible.filter((r) => !r.rated);
    return visible;
  }

  _funnel(c) {
    const steps = [
      { n: c.projects, k: 'website projects' },
      { n: c.closed_not_cancelled, k: 'closed, not cancelled' },
      { n: c.with_gross, k: 'with billing above zero — the rows below' },
      { n: c.usable, k: `usable, across ${count(c.usable_clients)} clients`, pool: true },
    ];
    return html`
      <div class="funnel">
        ${steps.map(
          (s) => html`
            <div class="step ${s.pool ? 'pool' : ''}">
              <div class="n">${count(s.n)}</div>
              <div class="k">${s.k}</div>
            </div>
          `,
        )}
      </div>
    `;
  }

  _row(r, editing = false) {
    const dateLabel = r.date_from === 'completed' ? 'Completed' : 'Started';
    const saving = this._saving.has(r.id);
    return html`
      <tr class=${[r.red ? 'red' : '', r.excluded ? 'excluded' : ''].filter(Boolean).join(' ')}>
        <td class="use">
          <input
            type="checkbox"
            .checked=${r.excluded}
            ?disabled=${saving || !this._canWrite}
            aria-label="Do not use ${r.number ?? r.name ?? 'this project'} for estimates or schedules"
            @change=${(e) => this._setExcluded(r, e.target.checked)}
          />
        </td>
        <td class="project">
          <div class="num-mono">${r.number ?? '—'}</div>
          ${r.name ?? ''}
        </td>
        <td>${r.client ?? html`<span class="unrated">— no client</span>`}</td>
        <td>
          ${r.date ? html`${mediumDate(r.date)} <span class="note">${dateLabel}</span>` : '—'}
          ${
            r.placeholder_start
              ? html`<span class="note">Start date is a placeholder (1900-01-01)</span>`
              : nothing
          }
        </td>
        <td class="size">
          ${
            this._canWrite
              ? html`<select
                  aria-label=${`Size of ${r.number ?? 'this project'}`}
                  ?disabled=${this._saving.has(`size:${r.id}`)}
                  @change=${(e) => this._setSize(r, e.target.value)}
                >
                  <option value="" ?selected=${!r.local_size_tier}>
                    ${
                      r.derived_size_tier
                        ? `${sizeTierLabel(r.derived_size_tier)} (from hours)`
                        : 'Unsized'
                    }
                  </option>
                  ${SIZE_TIERS.map(
                    (t) =>
                      html`<option value=${t} ?selected=${r.local_size_tier === t}>
                        ${sizeTierLabel(t)}
                      </option>`,
                  )}
                </select>`
              : html`<span
                  >${
                    r.size_tier ? sizeTierLabel(r.size_tier) : html`<span class="unrated">—</span>`
                  }</span
                >`
          }
          ${
            // Worth marking: this project's size is somebody's judgment, not its hours.
            r.size_source === 'set'
              ? html`<span
                  class="src"
                  title=${r.size_set_by ? `Set by ${r.size_set_by}` : 'Set here'}
                  >set</span
                >`
              : nothing
          }
        </td>
        <td class="num">
          ${r.org_complexity ?? html`<span class="unrated">—</span>`}
          ${
            // Only the local case is marked. A rating from Workamajig is the expected one and
            // needs no badge; a rating somebody typed here is the one a reader should be able
            // to see at a glance, because it exists nowhere else.
            r.rating_source === 'local'
              ? html`<span class="src" title="Set in WaaG; not in Workamajig">here</span>`
              : nothing
          }
        </td>
        <td class="num">${r.human_factor ?? html`<span class="unrated">—</span>`}</td>
        <td class="num">${money(r.gross)}</td>
        <td>
          ${
            r.usable
              ? html`<span class="usable">Usable</span>`
              : html`<span class="reason">
                  ${r.red ? html`<span class="marker" aria-hidden="true">●</span>` : nothing}
                  <span>${r.reason_label}</span>
                </span>`
          }
          ${
            r.excluded && r.excluded_note
              ? html`<span class="note">${r.excluded_note}</span>`
              : nothing
          }
          ${
            r.excluded && r.excluded_by
              ? html`<span class="note">Set by ${r.excluded_by}</span>`
              : nothing
          }
          ${
            r.company_id && this._canRate && !editing
              ? html`<button class="rate" @click=${() => this._startEditing(r)}>
                  ${r.rated ? 'Change rating' : `Rate ${r.client}`}
                </button>`
              : nothing
          }
        </td>
      </tr>
    `;
  }

  /** A red row, plus its editor when it is the one being rated. */
  /**
   * A row, plus the editor when this is the row it was opened on. The button is suppressed on
   * every row of the client being edited, not just this one: one open editor per client, and a
   * second entry point to the same write would only ever cause confusion.
   */
  _rowGroup(r) {
    const editingClient = this._editing !== null && this._editing === r.company_id;
    const editingHere = editingClient && this._editingRow === r.id;
    return html`${this._row(r, editingClient)}${editingHere ? this._editor(r) : nothing}`;
  }

  render() {
    if (this._error) return html`<div class="card"><p class="error">${this._error}</p></div>`;
    if (!this._data) return html`<div class="card"><p class="state">Loading…</p></div>`;

    const { counts } = this._data;
    const rows = this._rows;
    const visible = this._visible;
    const hidden = this._hidden;
    const filters = [
      { key: 'all', label: 'All' },
      { key: 'rated', label: 'Rated client' },
      { key: 'unrated', label: 'Unrated client' },
    ];

    return html`
      <div class="card">
        <div class="eyebrow">Data Hygiene</div>
        <h2>What can price a website</h2>
        <p class="lede">
          Every <strong>closed, billed</strong> website project, newest first, with the two ratings
          its <strong>client</strong> carries. A row is red when that client is missing either
          rating — the one thing this screen can fix, and the reason ${count(counts.client_unrated)}
          otherwise-usable projects are outside the pool. Tick <em>Do not use</em> to keep a project
          out of estimates and schedules for good.
        </p>
        <p class="lede">
          Not listed: ${count(counts.open)} still open, ${count(counts.cancelled)} cancelled,
          ${count(counts.zero_gross)} that billed
          nothing${
            counts.gross_unavailable
              ? html`, and ${count(counts.gross_unavailable)} that Workamajig holds as
                  <em>inactive</em>, for which it reports no billed total at all`
              : nothing
          }
          — counted above, but none of them is a question this screen can
          answer${
            counts.excluded
              ? html`, and ${count(counts.excluded)} marked <em>do not use</em>, which
                  <strong>Show all</strong> brings back so the box can be cleared`
              : nothing
          }.
          Web support and retainers are a different product and are not here at all.
        </p>

        ${
          this._data.rating_write?.enabled === false && this._data.rating_write?.reason
            ? html`<p class="notice">${this._data.rating_write.reason}</p>`
            : nothing
        }
        ${
          this._writeError
            ? html`<p class="write-error" role="alert">${this._writeError}</p>`
            : nothing
        }
        ${this._funnel(counts)}

        <div class="controls">
          <span class="segmented" role="group" aria-label="Filter by client rating">
            ${filters.map(
              (f) => html`
                <button
                  type="button"
                  aria-pressed=${this._filter === f.key ? 'true' : 'false'}
                  @click=${() => (this._filter = f.key)}
                >
                  ${f.label}
                </button>
              `,
            )}
          </span>
          ${
            hidden || this._showAll
              ? html`<button
                  class="show-all"
                  @click=${() => (this._showAll = !this._showAll)}
                  title="Projects marked Do Not Use are out of the pool by a decision already taken"
                >
                  ${this._showAll ? 'Hide do not use' : 'Show all'}
                </button>`
              : nothing
          }
          <span class="shown">
            ${count(rows.length)} of ${count(visible.length)}
            shown${hidden ? html` · ${count(hidden)} hidden as do not use` : nothing}${
              this._showAll && counts.excluded
                ? html` · including ${count(counts.excluded)} marked do not use`
                : nothing
            }${
              counts.placeholder_starts
                ? html` · ${count(counts.placeholder_starts)} with a placeholder start date`
                : nothing
            }
          </span>
          ${
            this._canWrite
              ? nothing
              : html`<span class="shown">Do not use is a planner action.</span>`
          }
        </div>

        <div class="scroll">
          <table>
            <caption class="sr-only" hidden>
              Website projects and whether each can price a new one
            </caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  class="use"
                  title="Exclude this project from estimates and schedules"
                >
                  Do not<br />use
                </th>
                <th scope="col">Project</th>
                <th scope="col">Client</th>
                <th scope="col">Completed / Started</th>
                <th scope="col">Size</th>
                <th scope="col">Org complexity</th>
                <th scope="col">Human factor</th>
                <th scope="col">Total billing</th>
                <th scope="col">Usable</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map((r) => this._rowGroup(r))}
            </tbody>
          </table>
          ${
            rows.length
              ? nothing
              : html`<p class="empty">
                  No projects match this
                  filter.${
                    hidden
                      ? html` ${count(hidden)} are hidden as do not use —
                          <button class="link" @click=${() => (this._showAll = true)}>
                            show all</button
                          >.`
                      : nothing
                  }
                </p>`
          }
        </div>
      </div>
    `;
  }
}

customElements.define('estimate-hygiene', EstimateHygiene);
